import {Packer} from "docx";
import type {Editor} from "slate";

import type {GuideComponents} from "@repo/guide-core";

import {getGraphClient} from "~/shared/auth/providers/microsoft/client.ts";
import {
    getOrCreateFolder,
    saveDocumentToOneDrive,
} from "~/shared/auth/providers/microsoft/microsoft-graph-api.ts";

import {getDocumentTitle} from "../slate/helpers/editor-helpers.ts";
import {clearUnsavedChanges} from "../slate/helpers/insertion-helpers.ts";

import {DEFAULT_DOCUMENT_NAME, KM_FOLDER_NAME} from "./constants.ts";
import createDocFromEditor from "./export-contents.ts";
import createPPTxFromSlides from "./pptx/export-pptx.ts";

/**
 * Save an editor as a docx document to OneDrive.
 *
 * @param editor The editor to be saved.
 * @returns A promise yielding the URL of the saved document.
 */
export const saveToMsOneDrive = async (editor: Editor): Promise<string> => {
    const graphClient = await getGraphClient();

    const kmFolder = await getOrCreateFolder(graphClient, KM_FOLDER_NAME);

    clearUnsavedChanges(editor);
    const doc = createDocFromEditor(editor, "ms-onedrive");
    const file = await Packer.toBlob(doc);
    const title = getDocumentTitle(editor);
    const filename = `${title}.docx`;

    const resp = await saveDocumentToOneDrive(
        graphClient,
        filename,
        DEFAULT_DOCUMENT_NAME,
        file,
        kmFolder,
    );
    return resp.webUrl;
};

/**
 * Create a pptx document from JSON content and save it to OneDrive.
 *
 * @param editor The editor to be saved.
 * @returns A promise yielding the URL of the saved document.
 */
export const savePresentationToOneDrive = async (
    editor: Editor,
    slides: GuideComponents["CreateSlides"]["output"]["slides"],
): Promise<string> => {
    const graphClient = await getGraphClient();

    const kmFolder = await getOrCreateFolder(graphClient, KM_FOLDER_NAME);

    const file = await createPPTxFromSlides(slides);
    const title = getDocumentTitle(editor);
    const filename = `${title}.pptx`;

    const resp = await saveDocumentToOneDrive(
        graphClient,
        filename,
        DEFAULT_DOCUMENT_NAME,
        file,
        kmFolder,
    );
    return resp.webUrl;
};
